import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "../../context/AuthContext";
import countryData from "../../Data/countryCode.json";
import logo from "../../assets/logo/logo.jpeg";
import Field from "../../Components/Auth/Field";
import type { Student } from "../../types/student";

interface FormData {
  name: string;
  email: string;
  country: string;
  countryCode: string;
  phoneNumber: string;
  password: string;
}

function computeProfileCompletion(u: Partial<Student>): number {
  const personal: [boolean, number][] = [
    [!!u.name?.trim(), 5],
    [!!u.email?.trim(), 5],
    [!!u.phoneNumber?.trim(), 5],
    [!!u.ic?.trim(), 5],
    [!!u.dob?.trim(), 5],
    [!!u.gender?.trim(), 5],
    [!!u.state?.trim(), 5],
    [!!u.countryCode?.trim(), 5],
  ];
  const academic: [boolean, number][] = [
    [
      !!(
        u.school &&
        typeof u.school === "object" &&
        u.school.schoolName.trim()
      ),
      10,
    ],
    [
      !!(
        u.school &&
        typeof u.school === "object" &&
        u.school.schoolYear.trim()
      ),
      10,
    ],
    [(u.resultDetails || u.spmGrades || []).length > 0, 10],
  ];
  const preferences: [boolean, number][] = [
    [!!u.pathway?.trim(), 10],
    [(u.interests?.length ?? 0) > 0, 10],
    [!!u.budget?.trim(), 10],
  ];

  return [...personal, ...academic, ...preferences].reduce(
    (sum, [ok, pts]) => sum + (ok ? pts : 0),
    0,
  );
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormData>({
    name: "",
    email: "",
    country: "Malaysia",
    countryCode: "+60",
    phoneNumber: "",
    password: "",
  });
  const [submitError, setSubmitError] = useState("");

  const update = (key: keyof FormData, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setSubmitError("");

    // ── Validation ──────────────────────────────────────────────────────────
    const nameRegex = /^[A-Za-z]+( [A-Za-z]+)*$/;
    if (!nameRegex.test(form.name.trim())) {
      setSubmitError(
        "Name must not contain numbers, symbols, or extra whitespace.",
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setSubmitError("Please enter a valid email address.");
      return;
    }

    const phoneRegex = /^\d+$/;
    if (!phoneRegex.test(form.phoneNumber)) {
      setSubmitError("Phone number must only contain numbers");
      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(form.password)) {
      setSubmitError(
        "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character",
      );
      return;
    }
    // ────────────────────────────────────────────────────────────────────────

    setLoading(true);

    try {
      const referralCode = new URLSearchParams(window.location.search)
        .get("ref")
        ?.trim()
        .toLowerCase();

      const success = await register({
        ...form,
        profileCompletion: computeProfileCompletion(form),
        ...(referralCode ? { referralCode } : {}),
      });
      if (!success) {
        setSubmitError("Registration failed. Please try again.");
        return;
      }
      setStep(4);
    } catch (error: any) {
      console.error("Registration failed:", error);

      switch (error.code) {
        case "auth/email-already-in-use":
          setSubmitError("This email address is already registered.");
          break;

        case "auth/network-request-failed":
          setSubmitError(
            "Network error. Please check your internet connection.",
          );
          break;

        case "permission-denied":
          setSubmitError("Unable to save registration data. Please try again.");
          break;

        default:
          setSubmitError(
            error.message || "Registration failed. Please try again.",
          );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4 py-10"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="w-full max-w-lg">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center">
          <div className="w-14 h-14 flex items-center justify-center">
            <img src={logo} alt="scs education logo" />
          </div>
        </Link>

        <AnimatePresence mode="wait">
          {/* STEP 1: Account */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-900/5 p-8"
            >
              <h2
                className="text-[28px] text-slate-900 tracking-tight mb-1"
                style={{ fontWeight: 800 }}
              >
                Create your account
              </h2>
              <p
                className="text-[13px] text-slate-500 mb-7"
                style={{ fontWeight: 400 }}
              >
                Already have one?{" "}
                <Link
                  to="/login"
                  className="text-violet-600"
                  style={{ fontWeight: 600 }}
                >
                  Sign in
                </Link>
              </p>

              <div className="flex flex-col gap-4">
                <Field
                  label="Full Name (as per IC) *"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  placeholder="Ain Nabila Binti Ahmad"
                  required
                />
                <Field
                  label="Email Address *"
                  type="email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  placeholder="you@email.com"
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label="Country Code *"
                    type="select"
                    value={form.countryCode}
                    onChange={(v) => update("countryCode", v)}
                    options={countryData.map((c) => ({
                      label: `${c.countryName} (+${c.countryCode})`,
                      value: `+${c.countryCode}`,
                    }))}
                    required
                  />
                  <Field
                    label="Phone Number *"
                    type="tel"
                    value={form.phoneNumber}
                    onChange={(v) => update("phoneNumber", v)}
                    placeholder="123456789"
                    required
                  />
                </div>
                <Field
                  label="Password *"
                  type="password"
                  value={form.password}
                  onChange={(v) => update("password", v)}
                  placeholder="Min. 8 chars incl. uppercase, lowercase, number & symbol"
                  required
                />
              </div>
              {submitError && (
                <div
                  className="text-red-500 text-[13px] text-center"
                  style={{ fontWeight: 500 }}
                >
                  {submitError}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full mt-6 py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] flex items-center justify-center gap-2 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all disabled:opacity-60 cursor-pointer"
                style={{ fontWeight: 600 }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Create My Profile <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </motion.div>
          )}

          {/* STEP 4: Done */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-900/5 p-10 text-center"
            >
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-violet-500/30">
                <span className="text-4xl">🎉</span>
              </div>
              <h2
                className="text-[32px] text-slate-900 tracking-tight mb-3"
                style={{ fontWeight: 900 }}
              >
                You're in, {form.name}!
              </h2>
              <p
                className="text-[14px] text-slate-500 mb-8 max-w-xs mx-auto leading-relaxed"
                style={{ fontWeight: 400 }}
              >
                Your profile is set up. A dedicated counselor will reach out to
                you within 24 hours. Let's find your dream university.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] flex items-center justify-center gap-2 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 hover:-translate-y-0.5 transition-all cursor-pointer"
                  style={{ fontWeight: 600 }}
                >
                  Go to My Dashboard <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
